"use client"
import React, { createContext, useContext } from 'react';
import useToast from '../../hooks/useToast';
import ToastContainer from './ToastContainer';

interface ToastContextType {
  toast: {
    success: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
    error: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
    info: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
    warning: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
  };
  // Keep old methods for backward compatibility
  showSuccessToast: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
  showErrorToast: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
  showInfoToast: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
  showWarningToast: (titleOrMessage: string, messageOrDuration?: string | number, duration?: number) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const {
    toasts,
    removeToast,
    toast,
    showSuccessToast,
    showErrorToast,
    showInfoToast,
    showWarningToast,
  } = useToast();

  return (
    <ToastContext.Provider
      value={{
        toast,
        showSuccessToast,
        showErrorToast,
        showInfoToast,
        showWarningToast,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export default ToastProvider;